import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  MenuItem,
  Chip,
  Collapse,
  Box,
  Divider,
  Button,
} from "@mui/material";
import {
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";
import ProductSection from "../ProductSection";

// Sample warehouses for dropdown
const SAMPLE_WAREHOUSES = [
  { id: 1, name: "Main Warehouse", location: "Mumbai" },
  { id: 2, name: "North Warehouse", location: "Delhi" },
  { id: 3, name: "South Warehouse", location: "Chennai" },
];

const InventoryManagement = ({ productData, updateProductData, errors }) => {
  const [inventory, setInventory] = useState([]);
  const [showAddInventory, setShowAddInventory] = useState(false);
  const [expandedInventory, setExpandedInventory] = useState(null);
  const [inventoryData, setInventoryData] = useState({
    warehouse: "",
    quantity: 0,
    availableQuantity: 0,
    reservedQuantity: 0,
    damagedQuantity: 0,
    minStockLevel: 0,
    maxStockLevel: 0,
    batchDetails: [],
    variant: null,
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [batchData, setBatchData] = useState({
    batch: "",
    quantity: 0,
  });
  const [editingBatchIndex, setEditingBatchIndex] = useState(null);
  const isInitialMount = React.useRef(true);

  // Initialize inventory from productData only on component mount
  useEffect(() => {
    if (isInitialMount.current) {
      if (productData.inventory) {
        setInventory(productData.inventory);
      }
      isInitialMount.current = false;
    }
  }, [productData.inventory]);

  const handleInventoryChange = (field, value, type = "text") => {
    setInventoryData({
      ...inventoryData,
      [field]: type === "number" ? parseFloat(value) || 0 : value,
    });
  };

  const handleBatchChange = (field, value, type = "text") => {
    setBatchData({
      ...batchData,
      [field]: type === "number" ? parseFloat(value) || 0 : value,
    });
  };

  const handleAddInventory = () => {
    if (editingIndex !== null) {
      // Update existing inventory
      const updatedInventory = [...inventory];
      updatedInventory[editingIndex] = inventoryData;
      setInventory(updatedInventory);
      setEditingIndex(null);
      updateProductData({ inventory: updatedInventory });
    } else {
      // Add new inventory
      setInventory([...inventory, inventoryData]);
      updateProductData({ inventory: [...inventory, inventoryData] });
    }

    // Reset form
    setInventoryData({
      warehouse: "",
      quantity: 0,
      availableQuantity: 0,
      reservedQuantity: 0,
      damagedQuantity: 0,
      minStockLevel: 0,
      maxStockLevel: 0,
      batchDetails: [],
      variant: null,
    });
    setShowAddInventory(false);
  };

  const handleEditInventory = index => {
    setInventoryData(inventory[index]);
    setEditingIndex(index);
    setShowAddInventory(true);
    setExpandedInventory(null);
  };

  const handleDeleteInventory = index => {
    const updatedInventory = [...inventory];
    updatedInventory.splice(index, 1);
    setInventory(updatedInventory);
    updateProductData({ inventory: updatedInventory });
  };

  const handleAddBatch = () => {
    if (editingBatchIndex !== null) {
      // Update existing batch
      const updatedBatchDetails = [...inventoryData.batchDetails];
      updatedBatchDetails[editingBatchIndex] = batchData;
      setInventoryData({
        ...inventoryData,
        batchDetails: updatedBatchDetails,
      });
      setEditingBatchIndex(null);
    } else {
      // Add new batch
      setInventoryData({
        ...inventoryData,
        batchDetails: [...inventoryData.batchDetails, batchData],
      });
    }

    // Reset form
    setBatchData({
      batch: "",
      quantity: 0,
    });
    setShowAddBatch(false);
  };

  const handleEditBatch = index => {
    setBatchData(inventoryData.batchDetails[index]);
    setEditingBatchIndex(index);
    setShowAddBatch(true);
  };

  const handleDeleteBatch = index => {
    const updatedBatchDetails = [...inventoryData.batchDetails];
    updatedBatchDetails.splice(index, 1);
    setInventoryData({
      ...inventoryData,
      batchDetails: updatedBatchDetails,
    });
  };

  const toggleExpandInventory = index => {
    setExpandedInventory(expandedInventory === index ? null : index);
  };

  // Get warehouse name by ID
  const getWarehouseName = id => {
    const warehouse = SAMPLE_WAREHOUSES.find(w => w.id === id);
    return warehouse ? warehouse.name : "Default Warehouse";
  };

  // Get variant name by ID
  const getVariantName = id => {
    if (!id) return "Default Size";
    const variant =
      productData.variants &&
      productData.variants.find(v => v.id === id || v.variantCode === id);
    return variant
      ? `${variant.size} (${variant.variantCode || ""})`
      : "Unknown Variant";
  };

  // Calculate total quantity
  const calculateTotalQuantity = () => {
    return inventory.reduce((total, inv) => total + inv.quantity, 0);
  };

  return (
    <div className="space-y-6">
      <ProductSection data="Inventory Management" />

      <div className="bg-blue-50 p-3 rounded-lg mb-4">
        <p className="text-blue-700 font-outfit text-xs flex items-center">
          <AlertTriangle size={14} className="mr-1" />
          Manage your product inventory across different warehouses. Track
          quantities, set stock levels, and manage batch details.
        </p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h3 className="text-sm font-medium font-outfit">
              Inventory Records
            </h3>
            {inventory.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Total Quantity: {calculateTotalQuantity()}
              </p>
            )}
          </div>

          <button
            onClick={() => {
              setShowAddInventory(true);
              setEditingIndex(null);
              setInventoryData({
                warehouse: "",
                quantity: 0,
                availableQuantity: 0,
                reservedQuantity: 0,
                damagedQuantity: 0,
                minStockLevel: 0,
                maxStockLevel: 0,
                batchDetails: [],
                variant: null,
              });
            }}
            className="px-3 py-1.5 bg-[#5652B0] text-white rounded-lg text-xs font-medium hover:bg-[#4A47A3] flex items-center gap-1">
            <Plus size={14} />
            Add Inventory
          </button>
        </div>

        {inventory.length === 0 && !showAddInventory ? (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-gray-500 text-sm mb-2">
              No inventory records added yet
            </p>
            <button
              onClick={() => {
                setShowAddInventory(true);
                setEditingIndex(null);
              }}
              className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE]">
              Add Your First Inventory Record
            </button>
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Warehouse
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Variant
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Quantity
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Available
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Min/Max Stock
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      width: "120px",
                    }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.map((inv, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell
                        sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                        {getWarehouseName(inv.warehouse)}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                        {getVariantName(inv.variant)}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                        {inv.quantity}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                        {inv.availableQuantity}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                        {inv.minStockLevel}/{inv.maxStockLevel}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpandInventory(index)}
                            sx={{ color: "#5652B0", padding: "4px" }}>
                            {expandedInventory === index ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditInventory(index)}
                            sx={{ color: "#5652B0", padding: "4px" }}>
                            <Edit size={14} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteInventory(index)}
                            sx={{ color: "#d32f2f", padding: "4px" }}>
                            <Trash2 size={14} />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={6}>
                        <Collapse
                          in={expandedInventory === index}
                          timeout="auto"
                          unmountOnExit>
                          <Box
                            sx={{
                              margin: 1,
                              padding: 1,
                              backgroundColor: "#f9fafb",
                              borderRadius: "0.5rem",
                            }}>
                            <div className="text-xs font-medium text-gray-700 mb-2">
                              Detailed Inventory
                            </div>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <div className="text-xs text-gray-500">
                                  Reserved Quantity
                                </div>
                                <div className="font-medium">
                                  {inv.reservedQuantity}
                                </div>
                              </Grid>
                              <Grid item xs={4}>
                                <div className="text-xs text-gray-500">
                                  Damaged Quantity
                                </div>
                                <div className="font-medium">
                                  {inv.damagedQuantity}
                                </div>
                              </Grid>
                              <Grid item xs={4}>
                                <div className="text-xs text-gray-500">
                                  Batch Details
                                </div>
                                <div className="font-medium">
                                  {inv?.batchDetails?.length} batches
                                </div>
                              </Grid>
                            </Grid>

                            {inv?.batchDetails?.length > 0 && (
                              <div className="mt-3">
                                <Divider sx={{ my: 1 }} />
                                <div className="text-xs font-medium text-gray-700 mb-2">
                                  Batch Details
                                </div>
                                <TableContainer
                                  component={Paper}
                                  sx={{
                                    borderRadius: "0.5rem",
                                    maxHeight: 150,
                                  }}>
                                  <Table size="small">
                                    <TableHead
                                      sx={{ backgroundColor: "#F9FAFB" }}>
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            fontFamily: "Outfit",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                          }}>
                                          Batch
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontFamily: "Outfit",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                          }}>
                                          Quantity
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {inv.batchDetails.map(
                                        (batch, batchIndex) => (
                                          <TableRow key={batchIndex}>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontSize: "0.7rem",
                                              }}>
                                              {batch.batch}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontSize: "0.7rem",
                                              }}>
                                              {batch.quantity}
                                            </TableCell>
                                          </TableRow>
                                        ),
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </div>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showAddInventory && (
          <div className="border border-gray-200 rounded-lg p-4 mt-4">
            <h3 className="text-sm font-medium font-outfit mb-3">
              {editingIndex !== null
                ? "Edit Inventory Record"
                : "Add Inventory Record"}
            </h3>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Warehouse"
                  value={inventoryData.warehouse}
                  onChange={e =>
                    handleInventoryChange("warehouse", e.target.value)
                  }
                  fullWidth
                  size="small"
                  required
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}>
                  {SAMPLE_WAREHOUSES.map(warehouse => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.location})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Variant"
                  value={inventoryData.variant}
                  onChange={e =>
                    handleInventoryChange("variant", e.target.value)
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}>
                  <MenuItem value={null}>Default Size</MenuItem>
                  {productData.variants &&
                    productData.variants.map((variant, index) => (
                      <MenuItem key={index} value={variant.variantCode}>
                        {variant.size} ({variant.variantCode || ""})
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Total Quantity"
                  type="number"
                  value={inventoryData.quantity}
                  onChange={e => {
                    const value = parseFloat(e.target.value) || 0;
                    handleInventoryChange("quantity", value, "number");
                    // handleInventoryChange("availableQuantity", value, "number");
                  }}
                  fullWidth
                  size="small"
                  required
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Available Quantity"
                  type="number"
                  value={inventoryData.availableQuantity}
                  onChange={e =>
                    handleInventoryChange(
                      "availableQuantity",
                      e.target.value,
                      "number",
                    )
                  }
                  fullWidth
                  size="small"
                  required
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Reserved Quantity"
                  type="number"
                  value={inventoryData.reservedQuantity}
                  onChange={e =>
                    handleInventoryChange(
                      "reservedQuantity",
                      e.target.value,
                      "number",
                    )
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Damaged Quantity"
                  type="number"
                  value={inventoryData.damagedQuantity}
                  onChange={e =>
                    handleInventoryChange(
                      "damagedQuantity",
                      e.target.value,
                      "number",
                    )
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Min Stock Level"
                  type="number"
                  value={inventoryData.minStockLevel}
                  onChange={e =>
                    handleInventoryChange(
                      "minStockLevel",
                      e.target.value,
                      "number",
                    )
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Max Stock Level"
                  type="number"
                  value={inventoryData.maxStockLevel}
                  onChange={e =>
                    handleInventoryChange(
                      "maxStockLevel",
                      e.target.value,
                      "number",
                    )
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Batch Details Section */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-xs font-medium font-outfit">
                  Batch Details
                </h4>
                <button
                  onClick={() => {
                    setShowAddBatch(true);
                    setEditingBatchIndex(null);
                    setBatchData({
                      batch: "",
                      quantity: 0,
                    });
                  }}
                  className="px-2 py-1 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE] flex items-center gap-1">
                  <Plus size={12} />
                  Add Batch
                </button>
              </div>

              {inventoryData?.batchDetails?.length === 0 && !showAddBatch ? (
                <div className="bg-gray-50 p-2 rounded-lg text-center">
                  <p className="text-gray-500 text-xs">
                    No batch details added yet
                  </p>
                </div>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{ borderRadius: "0.5rem", maxHeight: 150 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontFamily: "Outfit",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}>
                          Batch
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Outfit",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}>
                          Quantity
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Outfit",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            width: "80px",
                          }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryData.batchDetails.map((batch, batchIndex) => (
                        <TableRow key={batchIndex}>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.7rem" }}>
                            {batch.batch}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.7rem" }}>
                            {batch.quantity}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <IconButton
                                size="small"
                                onClick={() => handleEditBatch(batchIndex)}
                                sx={{ color: "#5652B0", padding: "2px" }}>
                                <Edit size={12} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBatch(batchIndex)}
                                sx={{ color: "#d32f2f", padding: "2px" }}>
                                <Trash2 size={12} />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}

                      {showAddBatch && (
                        <TableRow>
                          <TableCell>
                            <TextField
                              placeholder="Batch ID"
                              value={batchData.batch}
                              onChange={e =>
                                handleBatchChange("batch", e.target.value)
                              }
                              size="small"
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: "0.5rem",
                                  fontFamily: "Outfit",
                                  fontSize: "0.7rem",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              placeholder="Quantity"
                              type="number"
                              value={batchData.quantity}
                              onChange={e =>
                                handleBatchChange(
                                  "quantity",
                                  e.target.value,
                                  "number",
                                )
                              }
                              size="small"
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: "0.5rem",
                                  fontFamily: "Outfit",
                                  fontSize: "0.7rem",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <IconButton
                                size="small"
                                onClick={handleAddBatch}
                                disabled={
                                  !batchData.batch || batchData.quantity <= 0
                                }
                                sx={{
                                  color: "#5652B0",
                                  padding: "2px",
                                  "&.Mui-disabled": {
                                    color: "rgba(0, 0, 0, 0.26)",
                                  },
                                }}>
                                <Check size={12} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setShowAddBatch(false);
                                  setEditingBatchIndex(null);
                                }}
                                sx={{ color: "#d32f2f", padding: "2px" }}>
                                <X size={12} />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowAddInventory(false);
                  setEditingIndex(null);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleAddInventory}
                disabled={
                  !inventoryData.warehouse || inventoryData.quantity <= 0
                }
                className="px-3 py-1.5 bg-[#5652B0] text-white rounded-lg text-xs font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed">
                {editingIndex !== null ? "Update" : "Add"} Inventory
              </button>
            </div>
          </div>
        )}

        {!showAddInventory && inventory.length > 0 && (
          <button
            onClick={() => {
              setShowAddInventory(true);
              setEditingIndex(null);
              setInventoryData({
                warehouse: "",
                quantity: 0,
                availableQuantity: 0,
                reservedQuantity: 0,
                damagedQuantity: 0,
                minStockLevel: 0,
                maxStockLevel: 0,
                batchDetails: [],
                variant: null,
              });
            }}
            className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE] mt-3 flex items-center gap-1">
            <Plus size={14} />
            Add Inventory
          </button>
        )}
      </div>

      <div className="bg-yellow-50 p-3 rounded-lg">
        <p className="text-yellow-700 font-outfit text-xs flex items-center">
          <AlertTriangle size={14} className="mr-1" />
          Inventory management is critical for tracking stock levels and
          fulfilling orders efficiently. Make sure to set appropriate min/max
          stock levels for automatic reordering alerts.
        </p>
      </div>
    </div>
  );
};

export default InventoryManagement;
